import { useCallback } from 'react';
import { useTheme } from '@mui/material';
import { scaleLinear, type ScaleTime } from 'd3-scale';
import { select } from 'd3-selection';

import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import type { WindTimestep } from 'types/wind';

import { useUnmount } from 'react-use';
import styles from './LiveWindLineGraph.module.scss';
import { setupLiveWindGraph } from './setupLiveWindLineGraph';
import setupForecastedWindGraph from './setupForecastedWindGraph';

interface Args {
  liveWind: Array<LiveWindPoint>;
  forecastedWind: Array<WindTimestep>;
  ratings: Array<ConditionRating>;
  xScale: ScaleTime<number, number>;
  canvasRef: React.MutableRefObject<HTMLDivElement>;
  currentMarkerXPercentage: number;
}

const useSetupGraphCanvas = ({
  liveWind,
  forecastedWind,
  ratings,
  xScale,
  canvasRef,
  currentMarkerXPercentage,
}: Args) => {
  const theme = useTheme();

  const setupGraph = useCallback(() => {
    select(`.${styles.graphTicks}`).remove();
    select(`.${styles.graphSvg}`).remove();

    const canvas = select<HTMLDivElement, undefined>(`.${styles.graph}`);

    if (canvas && canvasRef?.current) {
      const yPadding = 26;

      const yMax = Math.max(
        ...liveWind.map((d) => Math.ceil(d.wind.speed)),
        ...forecastedWind.map((d) => Math.ceil(d.speed)),
      );

      const yMin = Math.min(
        ...liveWind.map((d) => Math.ceil(d.wind.speed)),
        ...forecastedWind.map((d) => Math.ceil(d.speed)),
      );

      const y = scaleLinear()
        .range([canvasRef.current.offsetHeight - yPadding * 1.5, yPadding])
        .domain([yMin, yMax]);

      const ticks = canvas.append('div').attr('class', styles.graphTicks);

      ticks
        .selectAll(`.${styles.graphTick}`)
        .data([yMin, Math.round(yMax / 2), yMax])
        .enter()
        .append('div')
        .attr('class', styles.graphTick)
        .style('top', (d) => `${y(d)}px`)
        .text((d) => d);

      canvas.append('svg').attr('class', styles.graphSvg);

      setupForecastedWindGraph(xScale, y, forecastedWind);
      setupLiveWindGraph(xScale, y, liveWind, ratings, theme, currentMarkerXPercentage);
    }
  }, [liveWind, forecastedWind, ratings, theme, canvasRef, xScale, currentMarkerXPercentage]);

  useUnmount(() => {
    select(`.${styles.graphTicks}`).remove();
    select(`.${styles.graphSvg}`).remove();
  });

  return {
    setupGraph,
  };
};

export default useSetupGraphCanvas;
